import { PARAGRAPH_KEY, TEXT_KEY } from '@techchange/pm-specs';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';

export const mergeTagTriggerPluginKey = new PluginKey('mergeTagTriggerPlugin');

const MERGE_TAG_REGEX = /{{([\w-.]*)$/;

/**
 * Plugin that manages the state for triggering a merge tag insertion.
 *
 * @returns {Object} Prosemirror Plugin object.
 */
const mergeTagTriggerPlugin = () =>

	new Plugin({

		key: mergeTagTriggerPluginKey,

		state: {

			// Initializes the plugin state
			init() {

				return {

					/**
				 	 * The start position of the merge tag text.
				 	 */
					mergeTagStart: null,

					/**
				 	 * The end position of the merge tag text.
				 	 */
					mergeTagEnd: null,

					/**
					 * The merge tag query text the user is typing.
					 */
					queryText: null,

					/**
					 * Flag specifying if the merge tag dropdown should be shown.
					 */
					isMergeTagActive: false,

				};

			},

			// Updates the plugin state given incoming editor transaction
			apply(tr, pluginState, prevState, nextState) {

				const { docChanged, selection } = tr;
				const { schema } = nextState;

				const newPluginState = Object.assign({}, pluginState);

				if (tr.getMeta('selectRef') !== undefined) {
					newPluginState.selectRef = tr.getMeta('selectRef');
				}

				// No need to parse for merge tags if content is unchanged
				if (!docChanged) {
					return newPluginState;
				}

				// Check if we are deactivating the merge tag state
				const deactivated = tr.getMeta('deactivateMergeTag');

				if (deactivated) {

					newPluginState.isMergeTagActive = false;
					newPluginState.mergeTagStart = null;
					newPluginState.mergeTagEnd = null;

					return newPluginState;

				}

				// Otherwise test the text node at the current selection
				// to see if it matches the merge tag pattern
				const { from, to, $from } = selection;

				const paragraphNodeType = schema.nodes[PARAGRAPH_KEY];
				const textNodeType = schema.nodes[TEXT_KEY];

				let queryText, mergeTagStart, mergeTagEnd;

				if (from === to) {

					const parentPos = $from.before();
					const parentNode = $from.parent;

					if (parentNode && parentNode.type === paragraphNodeType) {

						parentNode.descendants((node, offset) => {

							const absStart = parentPos + 1 + offset;
							const absEnd = absStart + node.nodeSize;

							if (node.type === textNodeType) {

								// Check if the current selection is within the text node
								if (from >= absStart && from <= absEnd) {

									// Get the text between the beginning of the text node
									//	up to the current cursor selection
									const text = $from.doc.textBetween(absStart, from);

									const match = text.match(MERGE_TAG_REGEX);

									if (match && match.length === 2) {

										// Get the merge tag text from the first capturing group
										queryText = match[1];

										// Get the absolute position of the first '{' character,
										mergeTagStart = absStart + match.index;

										// Get the absolute end position of the merge tag text being typed
										mergeTagEnd = absStart + match.index + match[0].length;

									}

								}

							}

						});

					}

				}

				// Check if there's a match
				if (queryText !== undefined && mergeTagStart !== undefined && mergeTagEnd !== undefined) {

					newPluginState.isMergeTagActive = true;
					newPluginState.queryText = queryText;
					newPluginState.mergeTagStart = mergeTagStart;
					newPluginState.mergeTagEnd = mergeTagEnd;

				// Otherwise deactivate
				} else {

					newPluginState.isMergeTagActive = false;
					newPluginState.queryText = null;
					newPluginState.mergeTagStart = null;
					newPluginState.mergeTagEnd = null;

				}

				return newPluginState;

			}

		},

		props: {

			handleTextInput(view, from, to, text) {

				const { isMergeTagActive } = this.getState(view.state);
				const { tr } = view.state;

				// Deactivate if user types non-word character in merge tag range
				if (isMergeTagActive && !text.match(/[\w-.]/)) {

					tr.setMeta('deactivateMergeTag', true);

					view.dispatch(tr);

				}

			},

			handleClick(view, pos) {

				const { isMergeTagActive, mergeTagEnd } = this.getState(view.state);
				const { tr } = view.state;

				// Deactivate if user clicks anywhere else besides the end of
				// the merge tag range
				if (isMergeTagActive && pos !== mergeTagEnd) {

					tr.setMeta('deactivateMergeTag', true);

					view.dispatch(tr);

				}

			},

			handleKeyDown(view, event) {

				const { isMergeTagActive, selectRef } = this.getState(view.state);
				const { tr } = view.state;

				// Don't override key down behavior if merge tag state is not active
				if (!isMergeTagActive) {
					return false;
				}

				switch (event.key) {

				case 'Esc': // IE/Edge value
				case 'Escape':

					tr.setMeta('deactivateMergeTag', true);
					view.dispatch(tr);
					break;

				case 'Left': // IE/Edge value
				case 'ArrowLeft':
				case 'Right': // IE/Edge value
				case 'ArrowRight':

					// Prevent default
					return true;

				case 'Enter':
				case 'Tab':
				case 'Down': // IE/Edge value
				case 'ArrowDown':
				case 'Up': // IE/Edge value
				case 'ArrowUp':

					if (selectRef) {

						// Trigger keydown event on react-select component
						selectRef.select.onKeyDown(event);

						// Prevent default
						return true;

					}

					break;

				default:

					return false;

				}

				return false;

			},

			decorations(state) {

				const { isMergeTagActive, mergeTagStart, mergeTagEnd } = this.getState(state);

				if (!isMergeTagActive) {
					return null;
				}

				// Wrap the current merge tag text in a span so we that we are
				// able to select it as a dom node and attach the dropdown popover
				return DecorationSet.create(state.doc, [
					Decoration.inline(mergeTagStart, mergeTagEnd, {
						nodeName: 'span',
					})
				]);

			},

		},

	});

export default mergeTagTriggerPlugin;
